import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface NotificationDTO {
  id: number;
  channel: string;
  recipient: string;
  template: string;
  status: 'PENDING' | 'SENT' | 'FAILED';
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
  createdAt: string;
}

export function NotificationsLog() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const list = useQuery({
    queryKey: ['notifications', statusFilter],
    queryFn: async () => {
      const { data } = await api.get<NotificationDTO[]>('/api/notifications', {
        params: statusFilter ? { status: statusFilter } : undefined,
      });
      return data;
    },
    refetchInterval: 15_000,
  });

  const retryMut = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/api/notifications/${id}/retry`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <div>
      <h2>Cola de notificaciones</h2>
      <p style={{ color: 'var(--color-muted)' }}>
        Worker procesa la cola cada 30 segundos con backoff exponencial. Forzar reintento envía
        inmediatamente.
      </p>

      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
        {[
          ['', 'Todas'],
          ['PENDING', 'Pendientes'],
          ['SENT', 'Enviadas'],
          ['FAILED', 'Fallidas'],
        ].map(([k, label]) => (
          <button
            key={k}
            className={statusFilter === k ? 'btn btn-primary' : 'btn'}
            onClick={() => setStatusFilter(k ?? '')}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={{ padding: '0.5rem 0.75rem' }}>Estado</th>
              <th style={{ padding: '0.5rem 0.75rem' }}>Canal</th>
              <th style={{ padding: '0.5rem 0.75rem' }}>Destinatario</th>
              <th style={{ padding: '0.5rem 0.75rem' }}>Plantilla</th>
              <th style={{ padding: '0.5rem 0.75rem' }}>Intentos</th>
              <th style={{ padding: '0.5rem 0.75rem' }}>Última actividad</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data?.map((n) => (
              <tr key={n.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  <span
                    style={{
                      background:
                        n.status === 'SENT'
                          ? 'var(--color-accent)'
                          : n.status === 'FAILED'
                            ? 'var(--color-danger)'
                            : '#cbd5e1',
                      color: '#fff',
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: '0.7rem',
                    }}
                  >
                    {n.status}
                  </span>
                </td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{n.channel}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{n.recipient}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}><code>{n.template}</code></td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{n.attempts}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  {n.sentAt
                    ? new Date(n.sentAt).toLocaleString('es-CO')
                    : new Date(n.createdAt).toLocaleString('es-CO')}
                  {n.lastError && (
                    <div style={{ color: 'var(--color-danger)', fontSize: '0.75rem', marginTop: 2 }}>
                      {n.lastError.slice(0, 120)}
                    </div>
                  )}
                </td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  {n.status !== 'SENT' && (
                    <button
                      className="btn"
                      style={{ padding: '2px 8px', fontSize: '0.8rem' }}
                      disabled={retryMut.isPending}
                      onClick={() => retryMut.mutate(n.id)}
                    >
                      Reintentar
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                  Sin notificaciones.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
